import { type ReactNode, useId } from 'react';

import { Field, FieldLabel } from '@air/primitive-field';

export type WorkspaceAgentFormFieldProps = {
  /** Receives the id to put on the control, which is what ties it to the label. */
  children: (controlId: string) => ReactNode;
  'data-testid'?: string;
  /** Marks a field that can be left alone, so a form of mostly-optional settings does not read as required. */
  isOptional?: boolean;
  label: string;
};

/**
 * A labelled control in an agent form.
 *
 * Takes its child as a function of the control id, and owns the id itself, so a field cannot be built
 * with a label pointing at nothing. Wrapping the control in the label would be simpler but only works
 * for inputs — the select renders a button, which cannot be labelled by nesting.
 *
 * Not memoised: the child is a function, so a new one arrives on every render of the caller and there
 * is nothing for `memo` to compare.
 */
export const WorkspaceAgentFormField = ({
  children,
  'data-testid': testId,
  isOptional,
  label,
}: WorkspaceAgentFormFieldProps) => {
  const controlId = useId();

  return (
    <Field data-testid={testId}>
      <FieldLabel htmlFor={controlId}>
        {label}
        {isOptional ? <span className="font-normal ads:text-metadata text-gray-9"> (optional)</span> : null}
      </FieldLabel>
      {children(controlId)}
    </Field>
  );
};

WorkspaceAgentFormField.displayName = 'WorkspaceAgentFormField';
